/* Integration Status dashboard — live CRM connection health, sync counts,
   retry, and sync history. Reads /api/crm/oauth/dashboard. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, RefreshCw, Activity, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

const NAMES = { clio: "Clio Manage", practicepanther: "PracticePanther", mycase: "MyCase", needles: "Needles / Neos" };

const HealthDot = ({ h }) => {
  const map = { healthy: "bg-green-500", error: "bg-red-500", reauth_required: "bg-amber-500" };
  return <span className={`h-2.5 w-2.5 rounded-full ${map[h] || "bg-slate-300"}`} />;
};

export function CrmSyncDashboard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/crm/oauth/dashboard").then(({ data }) => setData(data)).catch(() => setData({ connections: [], recent_failures: [] })), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const retry = async (provider) => {
    setBusy(provider);
    try { const { data } = await api.post("/crm/oauth/retry", { provider }); toast.success(`Requeued ${data.requeued} item(s)`); load(); }
    catch { toast.error("Retry failed"); } finally { setBusy(null); }
  };
  const toggle = async (provider, enabled) => {
    try { await api.post(`/crm/oauth/${provider}/keep-sync`, { enabled }); toast.success(`Keep-in-Sync ${enabled ? "enabled" : "paused"}`); load(); }
    catch { toast.error("Could not update sync"); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  if (!data.connections.length) return null;

  return (
    <div className="mt-10" data-testid="crm-sync-dashboard">
      <div className="flex items-center gap-2"><Activity className="w-5 h-5 text-cobalt" />
        <h2 className="font-heading text-lg font-bold text-navy">Integration Status</h2></div>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {data.connections.map((c) => (
          <div key={c.provider} className="bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`sync-card-${c.provider}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-heading font-bold text-navy"><HealthDot h={c.health} /> {NAMES[c.provider] || c.provider}</div>
              <label className="flex items-center gap-2 text-xs text-slate-500" data-testid={`sync-toggle-${c.provider}`}>
                <input type="checkbox" checked={c.keep_synced} onChange={(e) => toggle(c.provider, e.target.checked)} data-testid={`keepsync-checkbox-${c.provider}`} /> Keep in Sync
              </label>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center">
              {[["Synced", c.synced_total, CheckCircle2, "text-green-600"],
                ["Pending", c.pending, Clock, "text-amber-600"],
                ["Failed", c.failed, AlertTriangle, c.failed ? "text-red-600" : "text-slate-400"],
                ["Health", c.health === "healthy" ? "OK" : c.health === "error" ? "Err" : "Auth", Activity, "text-cobalt"]].map(([l, v, Icon, cls]) => (
                <div key={l} className="rounded-xl bg-slate-50 py-2" data-testid={`sync-stat-${c.provider}-${l.toLowerCase()}`}>
                  <Icon className={`w-4 h-4 mx-auto ${cls}`} />
                  <div className="font-heading font-extrabold text-navy text-sm mt-0.5">{v}</div>
                  <div className="text-[9px] uppercase text-slate-400">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-slate-400">Last successful sync: {c.last_sync_at ? new Date(c.last_sync_at).toLocaleString() : "—"}</div>
            <div className="mt-3 flex gap-2">
              {c.failed > 0 && <button onClick={() => retry(c.provider)} disabled={busy === c.provider} data-testid={`sync-retry-${c.provider}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover transition-all">{busy === c.provider ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Retry failed</button>}
            </div>
            {(c.history || []).length > 0 && (
              <details className="mt-3" data-testid={`sync-history-${c.provider}`}>
                <summary className="text-xs font-medium text-slate-500 cursor-pointer">Sync history ({c.history.length})</summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-auto">
                  {c.history.map((h, i) => (
                    <div key={i} className="text-[11px] flex justify-between">
                      <span className={h.status === "failed" ? "text-red-600" : "text-navy"}>{h.type || "import"} · {h.status}</span>
                      <span className="text-slate-400">{new Date(h.started_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
